import React, { useState } from 'react';
import { Card, Skeleton, Empty } from 'antd';
import { useRequest } from 'ahooks';
import { ResponsivePie } from '@nivo/pie';
import { getCtaValidationStatus } from '../../services/dashboardService.js';

export default function CtaValidationStatusCard({ dateParams }) {
  const [hiddenStatuses, setHiddenStatuses] = useState([]);
  
  const params = React.useMemo(() => ({ ...(dateParams || {}) }), [dateParams]);
  const { data, loading } = useRequest(() => getCtaValidationStatus(params), { 
    refreshDeps: [params.start, params.end, params.companyId] 
  });
  
  const rows = React.useMemo(() => (data?.data || data || []), [data]);
  
  const filteredRows = React.useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return rows.filter(r => !hiddenStatuses.includes(r.status));
  }, [rows, hiddenStatuses]);
  
  const allHidden = React.useMemo(() => {
    if (!rows || rows.length === 0) return false;
    return rows.every(r => hiddenStatuses.includes(r.status));
  }, [rows, hiddenStatuses]);
  
  const total = React.useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + (r?.count || 0), 0);
  }, [filteredRows]);

  const colorMap = {
    'Submitted': '#1677ff',
    'Under Review': '#fadb14',
    'Disruption In Progress': '#faad14',
    'Verified Scam': '#ff4d4f',
    'Verified Genuine': '#73d13d',
    'Notify Brand': '#13c2c2',
    'Scam Disrupted': '#722ed1',
  };

  const handleLegendClick = (status) => {
    setHiddenStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const getLegendColor = (status) => {
    const isHidden = hiddenStatuses.includes(status);
    return isHidden ? '#d9d9d9' : (colorMap[status] || '#ccc');
  };

  const getPieData = () => {
    if (allHidden) {
      return [];
    }
    return filteredRows.map((r) => ({ 
      id: r.status, 
      label: r.status, 
      value: r.count 
    }));
  };

  return (
    <Card title="CTA Validation Status">
      {loading ? (
        <Skeleton active />
      ) : (
        <div style={{ height: 240 }}>
          {(!rows || rows.length === 0) ? (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              overflow: 'hidden' 
            }}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <ResponsivePie
              data={getPieData()}
              innerRadius={0.65}
              padAngle={1}
              colors={({ id }) => colorMap[id] || '#ccc'}
              enableArcLinkLabels={false}
              arcLabelsSkipAngle={360}
              margin={{ top: 10, right: 170, bottom: 10, left: 10 }}

              onClick={(datum) => {
                handleLegendClick(datum.id);
              }}
              onMouseEnter={(datum, event) => {
                event.currentTarget.style.cursor = 'pointer';
              }}
              tooltip={({ datum }) => {
                const value = datum?.value ?? 0;
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return (
                  <div style={{ 
                    padding: '8px 10px', 
                    background: '#fff', 
                    border: '1px solid #d9d9d9', 
                    borderRadius: 6, 
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)', 
                    minWidth: 150 
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ 
                        width: 12, 
                        height: 12, 
                        background: datum?.color, 
                        borderRadius: 3, 
                        display: 'inline-block' 
                      }} />
                      <span style={{ fontWeight: 600 }}>{datum?.label || datum?.id}</span>
                    </div>
                    <div>
                      <span style={{ color: '#595959' }}>Count: </span>
                      <span style={{ fontWeight: 600 }}>{value}</span>
                    </div>
                    <div>
                      <span style={{ color: '#595959' }}>Percent: </span>
                      <span style={{ fontWeight: 600 }}>{pct}%</span>
                    </div>
                  </div>
                );
              }}
              legends={[{
                anchor: 'right', 
                direction: 'column', 
                translateX: 200, 
                itemWidth: 160, 
                itemHeight: 24, 
                itemsSpacing: 10, 
                symbolSize: 14,
                symbolShape: ({ x, y, size, fill, borderWidth, borderColor }) => (
                  <rect 
                    x={x} 
                    y={y} 
                    rx={3} 
                    ry={3} 
                    width={size} 
                    height={size} 
                    fill={fill} 
                    stroke={borderColor} 
                    strokeWidth={borderWidth} 
                  />
                ),

                onClick: (datum) => {
                  handleLegendClick(datum.id);
                },

                onMouseEnter: (datum, event) => {
                  event.currentTarget.style.cursor = 'pointer';
                },

                itemTextColor: (datum) => {
                  return hiddenStatuses.includes(datum.id) ? '#bfbfbf' : '#333';
                },
                itemOpacity: 1, //The legend remains visible forever

                fill: (datum) => {
                  return getLegendColor(datum.id);
                },
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: (datum) => {
                        return hiddenStatuses.includes(datum.id) ? '#a6a6a6' : '#000';
                      },
                      itemBackground: '#f5f5f5',
                    },
                  },
                ],
                //Legend data - always display all statuses
                data: rows.map(r => ({
                  id: r.status,
                  label: r.status,
                  color: getLegendColor(r.status),
                })),
              }]}
              //Add custom layers to handle display when all are hidden
              layers={
                allHidden 
                  ? [
                      'legends',
                      ({ centerX, centerY }) => (
                        <text
                          key="empty-message"
                          x={centerX}
                          y={centerY}
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{
                            fontSize: 14,
                            fill: '#999',
                            fontWeight: 400,
                          }}
                        >
                          No data visible.
                        </text>
                      )
                    ]
                  : ['arcs', 'arcLabels', 'arcLinkLabels', 'legends']
              }
            />
          )}
        </div>
      )}
    </Card>
  );
}
